import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { CONVERSATION_LENS_PACK } from "../../../../utils/terminology";
import { AnalysisProfile, LensGroup } from "../../types";

interface ProfileDialogProps {
  open: boolean;
  profile: AnalysisProfile | null;
  lensGroups: LensGroup[];
  profiles: AnalysisProfile[];
  onClose: () => void;
  onSave: (profile: Partial<AnalysisProfile>) => void;
}

export const ProfileDialog: React.FC<ProfileDialogProps> = ({
  open,
  profile,
  lensGroups,
  profiles,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: "",
    central_inbox_email: "",
    lens_group_name: "",
    status: "active" as "active" | "disabled"
  });

  const [errors, setErrors] = useState({
    name: "",
    central_inbox_email: "",
    lens_group_name: ""
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name,
        central_inbox_email: profile.central_inbox_email,
        lens_group_name: profile.lens_group_name || "",
        status: profile.status
      });
    } else {
      setFormData({
        name: "",
        central_inbox_email: "",
        lens_group_name: "",
        status: "active"
      });
    }
    setErrors({ name: "", central_inbox_email: "", lens_group_name: "" });
  }, [profile, open]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = () => {
    const newErrors = {
      name: "",
      central_inbox_email: "",
      lens_group_name: ""
    };

    if (!formData.name.trim()) {
      newErrors.name = "Group name is required";
    } else {
      // Check for duplicate names (excluding current profile when editing)
      const existingProfile = profiles.find(p =>
        p.name.toLowerCase() === formData.name.toLowerCase() &&
        p.id !== profile?.id
      );
      if (existingProfile) {
        newErrors.name = "An analysis group with this name already exists";
      }
    }

    if (!formData.central_inbox_email) {
      newErrors.central_inbox_email = "Central inbox email is required";
    } else if (!validateEmail(formData.central_inbox_email)) {
      newErrors.central_inbox_email = "Please enter a valid email address";
    }

    if (newErrors.name || newErrors.central_inbox_email || newErrors.lens_group_name) {
      setErrors(newErrors);
      return;
    }

    // Convert empty string to null for lens_group_name
    const submissionData = {
      ...formData,
      lens_group_name: formData.lens_group_name || null
    };
    onSave(submissionData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {profile ? "Edit Analysis Group" : "Add Analysis Group"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Group Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
            placeholder="e.g., Customer Service Team"
          />

          <TextField
            label="Central Inbox Email Address"
            type="email"
            value={formData.central_inbox_email}
            onChange={(e) =>
              setFormData({ ...formData, central_inbox_email: e.target.value })
            }
            error={!!errors.central_inbox_email}
            helperText={errors.central_inbox_email}
            fullWidth
            required
            placeholder="e.g., team-inbox@company.com"
          />

          <FormControl fullWidth error={!!errors.lens_group_name}>
            <InputLabel>{CONVERSATION_LENS_PACK}</InputLabel>
            <Select
              value={formData.lens_group_name}
              onChange={(e) =>
                setFormData({ ...formData, lens_group_name: e.target.value })
              }
              label={CONVERSATION_LENS_PACK}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {lensGroups.map((group) => (
                <MenuItem key={group.id} value={group.name}>
                  {group.name} ({group.source})
                </MenuItem>
              ))}
            </Select>
            {errors.lens_group_name && (
              <FormHelperText>{errors.lens_group_name}</FormHelperText>
            )}
            <FormHelperText>
              Select which {CONVERSATION_LENS_PACK} contains the conversation analysis
              templates for this group
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as "active" | "disabled"
                })
              }
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
